import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CloudAuthProvider } from "./context/CloudAuthContext";

// Public Pages
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import SocialProofBar from "./components/SocialProofBar";
import StatsSection from "./components/StatsSection";
import PartnersMarquee from "./components/PartnersMarquee";
import BenefitsSection from "./components/BenefitsSection";
import ComparisonSection from "./components/ComparisonSection";
import HowItWorks from "./components/HowItWorks";
import ResultsSection from "./components/ResultsSection";
import SaasSection from "./components/SaasSection";
import TestimonialSection from "./components/TestimonialSection";
import FAQSection from "./components/FAQSection";
import BookingSection from "./components/BookingSection";
import CTASection from "./components/CTASection";
import Footer from "./components/Footer";
import BookingModal from "./components/BookingModal";
import FloatingCTA from "./components/FloatingCTA";
import VoiceAgentSection from "./components/VoiceAgentSection";
import VoiceAgentPage from "./pages/VoiceAgentPage";

// Admin Pages
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardPage from "./pages/DashboardPage";
import LeadsPage from "./pages/LeadsPage";
import InvoicesPage from "./pages/InvoicesPage";
import IncomePage from "./pages/IncomePage";
import ExpensesPage from "./pages/ExpensesPage";
import TaxReportsPage from "./pages/TaxReportsPage";
import ClientsPage from "./pages/ClientsPage";
import SettingsPage from "./pages/SettingsPage";
import CloudServicePage from "./pages/CloudServicePage";
import IGFunnelPage from "./pages/IGFunnelPage";

// Funnel & Education Pages
import FunnelPage from "./pages/FunnelPage";
import EduLogin from "./pages/edu/EduLogin";
import EduDashboard from "./pages/edu/EduDashboard";
import CoursePage from "./pages/edu/CoursePage";
import { EduAuthProvider } from "./context/EduAuthContext";
import EduProtectedRoute from "./components/EduProtectedRoute";

// Cloud Pages (Ruonn Cloud)
import CloudLogin from "./pages/cloud/CloudLogin";
import CloudRegister from "./pages/cloud/CloudRegister";
import CloudAuthCallback from "./pages/cloud/CloudAuthCallback";
import CloudLayout from "./components/cloud/CloudLayout";
import CloudProtectedRoute from "./components/cloud/CloudProtectedRoute";
import VoicePage from "./pages/cloud/VoicePage";
import VoiceOverview from "./pages/cloud/VoiceOverview";
import VoiceCalls from "./pages/cloud/VoiceCalls";
import CallDetail from "./pages/cloud/CallDetail";
import VoiceAnalytics from "./pages/cloud/VoiceAnalytics";
import VoiceBilling from "./pages/cloud/VoiceBilling";
import VoicePrompts from "./pages/cloud/VoicePrompts";
import VoiceIntegrations from "./pages/cloud/VoiceIntegrations";
import VoiceSettings from "./pages/cloud/VoiceSettings";

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Header onOpenModal={() => setIsModalOpen(true)} />
      <HeroSection onOpenModal={() => setIsModalOpen(true)} />
      <SocialProofBar />
      <StatsSection />
      <PartnersMarquee />
      <div id="benefits">
        <BenefitsSection />
      </div>
      <VoiceAgentSection />
      <ComparisonSection onOpenModal={() => setIsModalOpen(true)} />
      <HowItWorks onOpenModal={() => setIsModalOpen(true)} />
      <ResultsSection />
      <SaasSection />
      <div id="testimonials">
        <TestimonialSection />
      </div>
      <div id="faq">
        <FAQSection />
      </div>
      <BookingSection />
      <CTASection onOpenModal={() => setIsModalOpen(true)} />
      <Footer />
      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <FloatingCTA onOpenModal={() => setIsModalOpen(true)} />
    </div>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppInner() {
  const location = useLocation();

  // Synchronously detect Google Auth callback (auth_token or session_id in URL hash)
  // This must run before ProtectedRoute to avoid race conditions
  if (location.hash?.includes('auth_token=') || location.hash?.includes('session_id=')) {
    return <CloudAuthCallback />;
  }

  return (
    <Routes>
      {/* If on edu subdomain, redirect root to /edu */}
      <Route path="/" element={
        window.location.hostname.startsWith('edu.')
          ? <Navigate to="/edu" replace />
          : <Home />
      } />
      <Route path="/voice-agent" element={<VoiceAgentPage />} />

      {/* IG Funnel public landing pages */}
      <Route path="/f" element={<FunnelPage />} />
      <Route path="/f/:ref" element={<FunnelPage />} />

      {/* Education — public */}
      <Route path="/edu/login" element={<EduLogin />} />

      {/* Education — protected */}
      <Route
        path="/edu"
        element={<EduProtectedRoute><EduDashboard /></EduProtectedRoute>}
      />
      <Route
        path="/edu/course/:slug"
        element={<EduProtectedRoute><CoursePage /></EduProtectedRoute>}
      />
      
      {/* Admin Login */}
      <Route path="/admin" element={<AdminLogin />} />
      
      {/* Protected Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="income" element={<IncomePage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="reports" element={<TaxReportsPage />} />
        <Route path="cloud-service" element={<CloudServicePage />} />
        <Route path="ig-funnel" element={<IGFunnelPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Cloud Login */}
      <Route path="/cloud/login" element={<CloudLogin />} />
      
      {/* Cloud Registration */}
      <Route path="/cloud/register" element={<CloudRegister />} />

      {/* Protected Cloud Routes */}
      <Route
        path="/cloud/*"
        element={
          <CloudProtectedRoute>
            <CloudLayout />
          </CloudProtectedRoute>
        }
      >
        <Route path="voice" element={<VoicePage />}>
          <Route index element={<VoiceOverview />} />
          <Route path="calls" element={<VoiceCalls />} />
          <Route path="calls/:callId" element={<CallDetail />} />
          <Route path="analytics" element={<VoiceAnalytics />} />
          <Route path="prompts" element={<VoicePrompts />} />
          <Route path="integrations" element={<VoiceIntegrations />} />
          <Route path="billing" element={<VoiceBilling />} />
          <Route path="settings" element={<VoiceSettings />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CloudAuthProvider>
          <EduAuthProvider>
            <BrowserRouter>
              <AppInner />
            </BrowserRouter>
          </EduAuthProvider>
        </CloudAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
